import { zodResolver } from "@hookform/resolvers/zod";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Box,
  FormControl,
  FormHelperText,
  Input,
  InputAdornment,
  Select,
  MenuItem,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { UserContext } from "../../../../../../App";
import { getManagers, validateCashDrawer } from "../../../../../../config/Services";
import { useSnackbar } from "../../../hooks/SnackbarProvider";
import usePrint from "../../../hooks/usePrint";
import { z } from "zod";
import "./CashDrawerModal.css";

const schema = z.object({
  userPin: z
    .string()
    .nonempty("Enter User Pin")
    .max(4, "User PIN must be 4 digits")
    .regex(/^\d{1,4}$/, "User PIN must be numeric"),
  managerPin: z
    .string()
    .nonempty("Enter Manager Pin")
    .max(4, "Manager PIN must be 4 digits")
    .regex(/^\d{1,4}$/, "Manager PIN must be numeric"),
  selectedManager: z.object({
    uidx: z.string().nonempty("Select Manager"),
    name: z.string().nonempty("Select Manager"),
    selected: z.boolean(),
  }),
});

const CashDrawerModal = ({ setDrawerModal }) => {
  const { color } = useContext(UserContext);
  const showSnackbar = useSnackbar();
  const { openDrawer } = usePrint();

  const [userVisible, setUserVisible] = useState(false);
  const [managerVisible, setManagerVisible] = useState(false);
  const [managerList, setManagerList] = useState([]);

  const locId = localStorage.getItem("locId");
  const userUidx = JSON.parse(localStorage.getItem("retailUserDetails")).uidx;

  const managersQuery = useQuery(["managers"], () => getManagers({ contextId: locId }), {
    retry: 2,
    refetchOnWindowFocus: false,
    onSuccess: (res) => {
      if (!res?.data?.data?.length) {
        showSnackbar("No managers found", "error");
        return;
      }
      const managers = res?.data?.data?.map((manager) => {
        return {
          uidx: manager.uidx,
          name: manager.first_name,
          selected: false,
        };
      });
      setManagerList(managers);
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const checkPin = useMutation({
    mutationFn: (payload) => validateCashDrawer(payload),
    onSuccess: (res) => {
      showSnackbar("Cash drawer opened successfully", "success");
      openDrawer();
      setDrawerModal(false);
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm({
    defaultValues: {
      userPin: "",
      managerPin: "",
      selectedManager: {},
    },
    mode: "onChange",
    resolver: zodResolver(schema),
  });

  const watchSelectedManager = watch("selectedManager");

  const handleManagerChange = (event) => {
    const selectedUidx = event.target.value;
    const selected = managerList?.find((m) => m?.uidx === selectedUidx);
    if (selected) {
      setValue("selectedManager", {
        uidx: selected?.uidx,
        name: selected?.name,
        selected: true,
      });
      setManagerList((prevState) =>
        prevState?.map((manager) => ({
          ...manager,
          selected: manager?.uidx === selectedUidx,
        }))
      );
      trigger("selectedManager");
    }
  };

  const handleSubmitData = () => {
    const payload = {
      managerUidx: getValues("selectedManager").uidx,
      managerPin: getValues("managerPin"),
      userUidx: userUidx,
      userPin: getValues("userPin"),
    };
    checkPin.mutate(payload);
  };

  const canSubmit = checkPin.isLoading || !isValid;

  return (
    <Box className={`cash-drawer-modal-container ${color === "dark" ? "dark" : ""}`}>
      {/* Header (Slate-900 look) */}
      <div className="cash-drawer-header">
        <div className="cash-drawer-icon-wrap">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="cash-drawer-lock-icon"
          >
            <circle cx="12" cy="16" r="1"></circle>
            <rect x="3" y="10" width="18" height="12" rx="2"></rect>
            <path d="M7 10V7a5 5 0 0 1 10 0v3"></path>
          </svg>
        </div>
        <h3 className="cash-drawer-title">Manager Access</h3>
        <p className="cash-drawer-subtitle">Authorization required to open cash drawer</p>
      </div>

      {/* Form Body */}
      <div className="cash-drawer-body">
        <form onSubmit={handleSubmit(handleSubmitData)} className="cash-drawer-form">
          {/* Select Manager Field */}
          <div className="cash-drawer-field">
            <label className="cash-drawer-label">Select Manager</label>
            <FormControl fullWidth error={!!errors?.selectedManager}>
              <Select
                value={watchSelectedManager?.uidx || ""}
                onChange={handleManagerChange}
                displayEmpty
                className="cash-drawer-select"
                variant="outlined"
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  PaperProps: {
                    sx: {
                      borderRadius: "12px",
                      marginTop: "4px",
                      maxHeight: 180,
                      overflowY: "auto",
                      boxShadow:
                        "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)",
                      border: `1px solid ${color === "dark" ? "#475569" : "#e2e8f0"}`,
                      backgroundColor: color === "dark" ? "#1e293b" : "#ffffff",
                      color: color === "dark" ? "#ffffff" : "#0f172a",
                      "&::-webkit-scrollbar": {
                        width: "6px",
                      },
                      "&::-webkit-scrollbar-track": {
                        background: color === "dark" ? "#1e293b" : "#f1f5f9",
                        borderRadius: "12px",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        background: color === "dark" ? "#475569" : "#cbd5e1",
                        borderRadius: "12px",
                        "&:hover": {
                          background: color === "dark" ? "#64748b" : "#94a3b8",
                        },
                      },
                      "& .MuiMenuItem-root": {
                        fontSize: "0.875rem",
                        padding: "8px 16px",
                        "&:hover": {
                          backgroundColor: color === "dark" ? "#334155" : "#f1f5f9",
                        },
                        "&.Mui-selected": {
                          backgroundColor: color === "dark" ? "#2563eb" : "#e0f2fe",
                          color: color === "dark" ? "#ffffff" : "#0369a1",
                          "&:hover": {
                            backgroundColor: color === "dark" ? "#1d4ed8" : "#bae6fd",
                          },
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled>
                  <span className="cash-drawer-placeholder">Select Manager</span>
                </MenuItem>
                {managerList?.map((manager) => (
                  <MenuItem key={manager?.uidx} value={manager?.uidx}>
                    {manager?.name}
                  </MenuItem>
                ))}
              </Select>
              {errors?.selectedManager && (
                <FormHelperText>
                  {errors?.selectedManager.message || "Select Manager"}
                </FormHelperText>
              )}
            </FormControl>
          </div>

          {/* Manager PIN Input */}
          <div className="cash-drawer-field">
            <label className="cash-drawer-label">Manager PIN</label>
            <FormControl fullWidth error={!!errors?.managerPin} variant="outlined">
              <div className="cash-drawer-input-wrapper">
                <Input
                  {...register("managerPin")}
                  type={managerVisible ? "number" : "password"}
                  className="cash-drawer-input"
                  placeholder="Enter Manager PIN"
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 4,
                    pattern: "[0-9]*",
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4);
                  }}
                  autoFocus
                  disableUnderline
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        onClick={() => setManagerVisible((prevState) => !prevState)}
                        edge="end"
                        size="small"
                      >
                        {managerVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </div>
              {errors?.managerPin && <FormHelperText>{errors?.managerPin?.message}</FormHelperText>}
            </FormControl>
          </div>

          {/* User PIN Input */}
          <div className="cash-drawer-field">
            <label className="cash-drawer-label">User PIN</label>
            <FormControl fullWidth error={!!errors?.userPin} variant="outlined">
              <div className="cash-drawer-input-wrapper">
                <Input
                  {...register("userPin")}
                  type={userVisible ? "text" : "password"}
                  className="cash-drawer-input"
                  placeholder="Enter User PIN"
                  inputProps={{
                    inputMode: "numeric",
                    maxLength: 4,
                    pattern: "[0-9]*",
                  }}
                  onInput={(e) => {
                    e.target.value = e.target.value.replace(/\D/g, "").slice(0, 4);
                  }}
                  disableUnderline
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        tabIndex={-1}
                        onClick={() => setUserVisible((prevState) => !prevState)}
                        edge="end"
                        size="small"
                      >
                        {userVisible ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                />
              </div>
              {errors?.userPin && <FormHelperText>{errors?.userPin?.message}</FormHelperText>}
            </FormControl>
          </div>

          {/* Action button */}
          <button type="submit" className="cash-drawer-submit-btn" disabled={canSubmit}>
            {checkPin?.isLoading ? (
              <CircularProgress color="success" sx={{ color: "#fff" }} size={20} />
            ) : (
              "Verify & Open Cash Drawer"
            )}
          </button>
        </form>
      </div>

      {/* Close button top-right */}
      <button type="button" className="cash-drawer-close-btn" onClick={() => setDrawerModal(false)}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="cash-drawer-close-icon"
        >
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </Box>
  );
};

export default CashDrawerModal;
